import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  postDataBS,
  getDataBS,
  deleteDataBS,
  putDataBS,
} from "../utils/awsService";
import Pagination from "../components/common/Pagination";
import "./AdminBlog.css";
const AdminBlog = () => {
  // State for blog post form
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [refreshPosts, setRefreshPosts] = useState(0);

  // Rich text editor states
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);

  // State for blog posts list
  const [blogPosts, setBlogPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  
  // Pagination state for admin
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Rich text editor ref
  const editorRef = useRef(null);
  const navigate = useNavigate();

  // Load existing blog posts
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setPostsLoading(true);

        const responseData = await getDataBS("/blog");

        // Extract blog posts data
        let blogPostsData = responseData.data["data"];

        // Transform data to extract and normalize specific fields
        const processedBlogPosts = blogPostsData
          .map((post) => ({
            id: post.id || null,
            title: post.title || "Untitled",
            content: post.content || "",
            author: post.author || "Unknown",
            createdAt: post.createdAt || new Date().toISOString(),
            category: post.category || "Uncategorized",
            featuredImage: post.featuredImage || "",
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setBlogPosts(processedBlogPosts);
        
        // Reset pagination to first page when posts are refreshed
        setCurrentPage(1);
      } catch (error) {
        setBlogPosts([]);
      } finally {
        setPostsLoading(false);
      }
    };

    fetchBlogPosts();
  }, [refreshPosts]);

  // Handle image URL change and preview
  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);
    if (url.trim()) {
      setImagePreview(url);
    } else {
      setImagePreview("");
    }
  };

  // Reset form
  const resetForm = () => {
    setTitle("");
    setContent("");
    setImageUrl("");
    setImagePreview("");
    setCategory("");
    setEditMode(false);
    setCurrentPostId(null);
  };

  // Handle text formatting buttons
  const handleFormatText = (formatType) => {
    if (!editorRef.current) return;

    const textarea = editorRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    let formatPrefix = "";
    let formatSuffix = "";

    switch (formatType) {
      case "bold":
        formatPrefix = "**";
        formatSuffix = "**";
        break;
      case "italic":
        formatPrefix = "_";
        formatSuffix = "_";
        break;
      case "strikethrough":
        formatPrefix = "~~";
        formatSuffix = "~~";
        break;
      case "link":
        const url = prompt("Enter the URL:");
        if (!url) return;
        formatPrefix = "[";
        formatSuffix = `](${url})`;
        break;
      default:
        return;
    }

    const selectedText = content.substring(start, end);
    const newText =
      content.substring(0, start) +
      formatPrefix +
      selectedText +
      formatSuffix +
      content.substring(end);

    setContent(newText);

    // Focus back on the editor after applying format
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + formatPrefix.length,
        end + formatPrefix.length
      );
    }, 0);
  };

  // Handle textarea selection change
  const handleSelectionChange = () => {
    if (!editorRef.current) return;

    setSelectionStart(editorRef.current.selectionStart);
    setSelectionEnd(editorRef.current.selectionEnd);
  };

  // Edit blog post
  const handleEditPost = (post) => {
    // Populate form with post data
    setTitle(post.title);
    setContent(
      post.content
        .replace(/<p>(.*?)<\/p>/g, "$1\n\n")
        .replace(/<h(\d)>(.*?)<\/h\d>/g, "#".repeat(parseInt("$1")) + " $2\n\n")
        .trim()
    );
    setImageUrl(post.featuredImage);
    setImagePreview(post.featuredImage);
    setCategory(post.category);
    setEditMode(true);
    setCurrentPostId(post.id);
    setUrl(post.url);

    // Scroll to the top of the form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      alert("Please fill in all required fields.");
      return;
    }

    const trimmedText = title.trim();
    const hyphenatedTitle = trimmedText.replace(/\s+/g, "-");

    setLoading(true);

    try {
      const formattedContent = formatContent(content);

      const postData = {
        title,
        blogId: url,
        content: formattedContent,
        featuredImage: imageUrl,
        category: category || "Uncategorized",
        author: "Mento",
        submittedAt: new Date().toISOString(),
      };

      let response;

      if (editMode) {
        // Add the createdAt date to maintain the original creation date
        const originalPost = blogPosts.find(
          (post) => post.id === currentPostId
        );
        postData.createdAt = originalPost.createdAt;
        postData.updatedAt = new Date().toISOString();

        // Update existing post
        response = await putDataBS(`/blog/${currentPostId}`, postData);
        alert("Blog post updated successfully!");
      } else {
        // Create new post
        postData.createdAt = new Date().toISOString();
        response = await postDataBS("/blog", postData);
        alert("Blog post created successfully!");
      }


      // Trigger a refresh of blog posts
      setRefreshPosts((prev) => prev + 1);
      
      // Reset pagination to first page
      setCurrentPage(1);

      // Reset form after successful submission
      resetForm();
    } catch (error) {
      console.error("Error with blog post operation:", error);

      let errorMessage = editMode
        ? "Failed to update blog post. "
        : "Failed to create blog post. ";

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Please try again later.";
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format content with headings, paragraphs and formatting
  const formatContent = (text) => {
    // First, convert markdown formatting to HTML
    let htmlContent = text
      // Convert bold
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      // Convert italic
      .replace(/_(.*?)_/g, "<em>$1</em>")
      // Convert strikethrough
      .replace(/~~(.*?)~~/g, "<del>$1</del>")
      // Convert links
      .replace(
        /\[(.*?)\]\((.*?)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      );

    // Split the content by lines
    const lines = htmlContent.split("\n");
    const formattedLines = [];
    let currentParagraph = [];

    // Process each line
    lines.forEach((line) => {
      const trimmedLine = line.trim();

      // Skip empty lines but use them as paragraph separators
      if (trimmedLine === "") {
        if (currentParagraph.length > 0) {
          formattedLines.push(currentParagraph.join(" "));
          currentParagraph = [];
        }
        return;
      }

      // Check if line is a heading (starts with # markdown style)
      if (trimmedLine.startsWith("#")) {
        // Push current paragraph if any
        if (currentParagraph.length > 0) {
          formattedLines.push(currentParagraph.join(" "));
          currentParagraph = [];
        }

        // Add heading with appropriate marker
        const headingLevel = trimmedLine.match(/^#+/)[0].length;
        if (headingLevel <= 6) {
          const headingText = trimmedLine.replace(/^#+\s*/, "").trim();
          formattedLines.push(
            `<h${headingLevel}>${headingText}</h${headingLevel}>`
          );
        } else {
          // If more than 6 #'s, treat as normal paragraph
          currentParagraph.push(trimmedLine);
        }
      }
      // Handle alternative heading format (equals or dash underlining)
      else if (trimmedLine.match(/^[=]+$/) && currentParagraph.length === 1) {
        const headingText = currentParagraph[0];
        formattedLines.push(`<h1>${headingText}</h1>`);
        currentParagraph = [];
      } else if (trimmedLine.match(/^[-]+$/) && currentParagraph.length === 1) {
        const headingText = currentParagraph[0];
        formattedLines.push(`<h2>${headingText}</h2>`);
        currentParagraph = [];
      }
      // Normal line, add to current paragraph
      else {
        currentParagraph.push(trimmedLine);
      }
    });

    // Add any remaining paragraph
    if (currentParagraph.length > 0) {
      formattedLines.push(currentParagraph.join(" "));
    }

    // Join paragraphs with double newlines, but keep HTML headings separate
    return formattedLines
      .map((line) => {
        // Wrap non-heading lines in paragraph tags
        if (!line.startsWith("<h") && !line.endsWith(">")) {
          return `<p>${line}</p>`;
        }
        return line;
      })
      .join("\n");
  };

  // Function to safely render HTML in preview
  const renderContentPreview = (htmlContent) => {
    return { __html: htmlContent };
  };

  // Delete blog post
  const handleDeletePost = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this blog post? This action cannot be undone."
      )
    ) {
      try {
        await deleteDataBS("/blog", id);

        // Refresh the blog posts by incrementing refreshPosts
        setRefreshPosts((prev) => prev + 1);
        
        // Reset pagination to first page
        setCurrentPage(1);

        // You can also update the UI immediately if desired
        setBlogPosts((prevPosts) => prevPosts.filter((post) => post.id !== id));

        // Optional: Show success message
        alert("Blog post deleted successfully");
      } catch (error) {
        console.error("Error deleting post:", error);
        alert("Failed to delete blog post.");
      }
    }
  };

  const handleBack = () => {
    navigate("/admin/admin-dashboard");
  };

  // Pagination handlers for admin
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to the blog list section
    const blogListSection = document.querySelector('.blog-list-section');
    if (blogListSection) {
      blogListSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Calculate pagination for admin
  const totalPages = Math.ceil(blogPosts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPosts = blogPosts.slice(startIndex, endIndex);

  return (
    <div className="admin-blog-container">
      <button onClick={handleBack} className="back-btn">
        ← Back to Dashboard
      </button>

      <h1 className="admin-blog-title">Blog Administration</h1>

      <div className="admin-blog-layout">
        <div className="blog-form-section">
          <h2 className="section-title">
            {editMode ? "Edit Blog Post" : "Create New Blog Post"}
          </h2>


          <form onSubmit={handleSubmit} className="blog-form">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter blog post title"
                required
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">Define Url*</label>
              <input
                type="text"
                id="url"
                value={url}
                onChange={(e) => {
                  // Replace any spaces with hyphens as the user types
                  const newValue = e.target.value.replace(/\s/g, "-");
                  setUrl(newValue);
                }}
                placeholder="use this '-' other then spaces"
                required
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <input
                type="text"
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Enter category (optional)"
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="imageUrl">Featured Image URL</label>
              <input
                type="url"
                id="imageUrl"
                value={imageUrl}
                onChange={handleImageUrlChange}
                placeholder="Enter image URL (optional)"
                className="form-control"
                disabled={loading}
              />

              {imagePreview && (
                <div className="image-preview-container">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="image-preview"
                    onError={() => {
                      alert("Image URL is invalid or inaccessible");
                      setImagePreview("");
                    }}
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => {
                      setImageUrl("");
                      setImagePreview("");
                    }}
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="content">Content *</label>
              <div className="content-help-text">
                <p>Use markdown-style headings:</p>
                <ul>
                  <li># Heading 1</li>
                  <li>## Heading 2</li>
                  <li>### Heading 3</li>
                </ul>
                <p>Separate paragraphs with blank lines.</p>
              </div>

              <div className="formatting-toolbar">
                <button
                  type="button"
                  onClick={() => handleFormatText("bold")}
                  title="Bold"
                  className="format-btn"
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  onClick={() => handleFormatText("italic")}
                  title="Italic"
                  className="format-btn"
                >
                  <em>I</em>
                </button>
                <button
                  type="button"
                  onClick={() => handleFormatText("strikethrough")}
                  title="Strikethrough"
                  className="format-btn"
                >
                  <del>S</del>
                </button>
                <button
                  type="button"
                  onClick={() => handleFormatText("link")}
                  title="Insert Link"
                  className="format-btn"
                >
                  🔗
                </button>
              </div>

              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onSelect={handleSelectionChange}
                onClick={handleSelectionChange}
                onKeyUp={handleSelectionChange}
                placeholder="Write your blog post content here... Use formatting buttons above or markdown syntax."
                required
                className="form-control content-textarea"
                rows="10"
                disabled={loading}
                ref={editorRef}
              />
            </div>

            {content && (
              <div className="form-group">
                <label>Content Preview</label>
                <div
                  className="content-preview"
                  dangerouslySetInnerHTML={renderContentPreview(
                    formatContent(content)
                  )}
                />
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading
                  ? editMode
                    ? "Updating..."
                    : "Publishing..."
                  : editMode
                  ? "Update Blog Post"
                  : "Publish Blog Post"}
              </button>
            </div>
          </form>
        </div>

        <div className="blog-list-section">
          <h2 className="section-title">Published Blog Posts</h2>

          {blogPosts.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={blogPosts.length}
            />
          )}

          {postsLoading ? (
            <div className="loading-posts">Loading blog posts...</div>
          ) : blogPosts.length === 0 ? (
            <div className="no-posts">
              No blog posts found. Create your first post!
            </div>
          ) : (
            <div className="blog-posts-list">
              {currentPosts.map((post) => (
                <div key={post.id} className="blog-post-item">
                  <div className="post-thumbnail">
                    <img 
                      src={post.featuredImage || '/default-blog-image.jpg'} 
                      alt={post.title} 
                    />
                  </div>
                  <div className="post-info">
                    <h3 className="post-title">{post.title}</h3>
                    <div className="post-meta">
                      <span className="post-category">{post.category}</span>
                      <span className="post-date">
                        {new Date(post.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="post-actions">
                    <button
                      className="edit-post-btn"
                      onClick={() => handleEditPost(post)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-post-btn"
                      onClick={() => handleDeletePost(post.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBlog;
